export default class Room {
  constructor() {
    this.audioOpQueue = Promise.resolve();
  }

  // Serializes audio track operations so concurrent toggles don't race
  runAudioOp(fn) {
    this.audioOpQueue = this.audioOpQueue.then(async () => {
      try {
        await fn();
      } catch (err) {
        console.error("Audio track op failed:", err);
        Rollbar.error("audio_track_op_failed", { error: err.toString() });
      }
    });
    return this.audioOpQueue;
  }

  async recreateAudioTrack(previousAudioOptions = null) {
    LocalStream._audio_track_updating = true;
    try {
      const oldTrack = localTracks.audioTrack;
      // First try to acquire the replacement without tearing down the current audio.
      // This keeps the user from losing audio entirely if Safari rejects the requested
      // constraints. If that fails, fall back to the older destructive release/reacquire
      // path to handle browsers that require the previous session to be closed first.
      let audioUpdateResult = await LocalStream.updateAudioTrack();
      if (!audioUpdateResult && oldTrack) {
        console.warn(
          "recreateAudioTrack: non-destructive update failed, retrying after releasing existing audio track",
        );
        Rollbar.warning("audio_track_retry_with_release");
        if (client && client.uid) {
          try {
            await client.unpublish([oldTrack]);
          } catch (e) {
            console.warn("unpublish old track failed:", e);
          }
        }
        try {
          oldTrack.stop();
          oldTrack.close();
        } catch (e) {}
        localTracks.audioTrack = null;
        audioUpdateResult = await LocalStream.updateAudioTrack({
          releaseExistingTracks: true,
        });
      }

      if (!audioUpdateResult || !audioUpdateResult.track) {
        if (oldTrack) {
          // Had a working track before but failed to recreate it — worth reporting.
          console.error(
            "recreateAudioTrack: updateAudioTrack returned no track",
          );
          Rollbar.error("audio_track_missing_after_update");
        }
        return;
      }
      const localAudioTrack = audioUpdateResult.track;

      let newTrack;
      try {
        // Use the track acquired with { exact } constraints so Agora transmits it
        // without re-routing through VPIO (which would silently override noiseSuppression).
        newTrack = AgoraRTC.createCustomAudioTrack({
          mediaStreamTrack: localAudioTrack,
        });
        if (client && client.uid) {
          await client.publish([newTrack]);
        }
      } catch (err) {
        console.error("recreateAudioTrack failed:", err);
        Rollbar.error("audio_track_create_failed", { error: err.toString() });
        if (newTrack) {
          try {
            newTrack.stop();
            newTrack.close();
          } catch (e) {
            console.warn("new track cleanup failed:", e);
          }
        }
        return;
      }

      if (oldTrack) {
        if (client && client.uid) {
          try {
            await client.unpublish([oldTrack]);
          } catch (e) {
            console.warn("unpublish replaced old track failed:", e);
          }
        }
        try {
          oldTrack.stop();
          oldTrack.close();
        } catch (e) {}
      }

      localTracks.audioTrack = newTrack;

      if (!audioUpdateResult.appliedExactly) {
        if (previousAudioOptions) {
          options.echo_cancellation = previousAudioOptions.echo_cancellation;
          options.noise_reduction = previousAudioOptions.noise_reduction;
          this.syncLocalAudioSettingUI();
        }
        this.show_error_message(
          "Your microphone stayed connected, but this browser could not apply that audio setting exactly.",
        );
      }
    } finally {
      LocalStream._audio_track_updating = false;
    }
  }

  syncLocalAudioSettingUI(id = options.uid) {
    this._updateListeningToggle(
      options.echo_cancellation ? "Speakers" : "Headphones",
    );

    const echoToggle = $(`#user-row-${id} .user_remove_echo`);
    echoToggle.toggleClass(
      "bg-indigo-500 dark:bg-indigo-700",
      !!options.echo_cancellation,
    );
    echoToggle.find("span").toggleClass("translate-x-3", !!options.echo_cancellation);
    echoToggle.attr("aria-checked", options.echo_cancellation ? "true" : "false");

    const noiseToggle = $(`#user-row-${id} .user_reduce_noise`);
    noiseToggle.toggleClass(
      "bg-indigo-500 dark:bg-indigo-700",
      !!options.noise_reduction,
    );
    noiseToggle.find("span").toggleClass("translate-x-3", !!options.noise_reduction);
    noiseToggle.attr("aria-checked", options.noise_reduction ? "true" : "false");
  }

  _updateListeningToggle(activeLabel) {
    const activeClasses =
      "listening-toggle-btn flex-1 rounded-lg px-3 py-2 text-xs font-semibold bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-sm border border-gray-300 dark:border-gray-800 flex items-center justify-center gap-1.5";
    const inactiveClasses =
      "listening-toggle-btn flex-1 rounded-lg px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center justify-center gap-1.5";

    document.querySelectorAll(".listening-toggle-btn").forEach((btn) => {
      btn.className =
        btn.dataset.label === activeLabel ? activeClasses : inactiveClasses;
    });
  }

  check_browser() {
    var isEdge =
      navigator.userAgent.indexOf("Edge") !== -1 &&
      (!!navigator.msSaveBlob || !!navigator.msSaveOrOpenBlob);
    var isOpera = !!window.opera || navigator.userAgent.indexOf("OPR/") !== -1;
    isFirefox =
      navigator.userAgent.toLowerCase().indexOf("firefox") > -1 &&
      "netscape" in window &&
      / rv:/.test(navigator.userAgent);
    isChrome =
      (!isOpera && !isEdge && !!navigator.webkitGetUserMedia) ||
      this.isElectron() ||
      navigator.userAgent.toLowerCase().indexOf("chrome/") !== -1;
    isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari && !isChrome && navigator.userAgent.indexOf("CriOS") !== -1) {
      isSafari = false;
      isChrome = true;
    }
    // if (Rosom.is_iOS()) { $("#use_browser").show() }
  }

  is_iOS() {
    // return true;
    return (
      [
        "iPad Simulator",
        "iPhone Simulator",
        "iPod Simulator",
        "iPad",
        "iPhone",
        "iPod",
      ].includes(navigator.platform) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
  }
  isElectron() {
    // Renderer process
    if (
      typeof window !== "undefined" &&
      typeof window.process === "object" &&
      window.process.type === "renderer"
    ) {
      return true;
    }

    // Main process
    if (
      typeof process !== "undefined" &&
      typeof process.versions === "object" &&
      !!process.versions.electron
    ) {
      return true;
    }

    // Detect the user agent when the `nodeIntegration` option is set to true
    if (
      typeof navigator === "object" &&
      typeof navigator.userAgent === "string" &&
      navigator.userAgent.indexOf("Electron") >= 0
    ) {
      return true;
    }

    return false;
  }

  start_recording_timer() {
    var new_time = Math.abs(
      Math.round(
        (Date.parse($("#started_at").attr("data-timestamp")) - Date.now()) /
          1000 +
          5,
      ),
    )
      .toString()
      .toHHMMSS();
    $("#timer").text(new_time);

    // Clear existing interval to prevent memory leak
    if (window.recording_interval) {
      clearInterval(window.recording_interval);
    }

    window.recording_interval = setInterval(function () {
      var new_time = Math.abs(
        Math.round(
          (Date.parse($("#started_at").attr("data-timestamp")) - Date.now()) /
            1000 +
            5,
        ),
      )
        .toString()
        .toHHMMSS();
      $("#timer").text(new_time);
    }, 1000);
  }

  stopping_recording() {
    clearInterval(window.local_checker);
    clearInterval(window.recording_interval);
    window.local_checker = undefined;
    window.recording_interval = undefined;
  }

  // this shoud work for guests and hosts
  async stop_showing_now_recording(stoppedRecordingId = null) {
    this.stopping_recording();
    // $("#room_panel .devices").slideDown(200);
    // $("#room_panel .toggle_devices").removeClass("fa-chevron-down").addClass("fa-chevron-up")
    $(".remote_camera_settings").prop("disabled", false);
    $(".remote_microphone_settings").prop("disabled", false);
    $(".enable_echo_button, .disable_echo_button").prop("disabled", false);
    $(".user_reduce_noise").prop("disabled", false);
    $(".user_remove_echo").prop("disabled", false);
    $("#video_setting").prop("disabled", false);
    $("#microphone_setting").prop("disabled", false);
    $(".disable_echo_cancellation, .enable_echo_cancellation").prop(
      "disabled",
      false,
    );
    $("#toggle_video").removeClass("disabled");
    $("#toggle_mic").removeClass("disabled");
    $("#max_quality_dropdown").prop("disabled", false);
    $("#frame_rate_dropdown").prop("disabled", false);
    $(".video_type_label").removeClass("opacity-40");
    $(".echo_row").css("opacity", "1");
    $(".settings_cant_be_changed").hide();

    console.log("stop showing recording");
    // Use the recording_id from the broadcast when available. This prevents a stale
    // stopped_recording message (for an old recording) from corrupting the state of
    // a new recording that has already started and updated the DOM recording_id.
    const domRecordingId = $("#recording_id").attr("data-recording-id");
    const isCurrentRecording =
      !stoppedRecordingId ||
      String(stoppedRecordingId) === String(domRecordingId);
    const recordingIdToStop = stoppedRecordingId
      ? String(stoppedRecordingId)
      : domRecordingId;

    if (isCurrentRecording && window.uploading) {
      window.uploading = false;
      window.localUploadComplete = false;
      window.localUploadPending = true;
      window.recordingNamed = false;
      $("#upload_complete_view").hide();
      $("#guest_close_button").hide();
      $("#upload_progress_view").show();
      var hasNameForm =
        document.getElementById("update_recording_name_form") !== null;
      if (!hasNameForm) {
        $("#still_uploading_local").show();
      }
      $("#recording_finished_actions").hide();
      $("#recording_finished").show();
      var new_button = `<i class="fas fa-circle p-2 text-red-600 rounded-full"></i>
          <span class="mx-1 dark:text-gray-600">Waiting on host <span class="hidden sm:inline">to record</span></span>`;
      $("#guest_start_recording_button").removeClass();
      $("#guest_start_recording_button")
        .html(new_button)
        .addClass("px-2 inline text-sm rounded");
    }
    // Always send recording_stopped with the correct ID, even if DOM has moved on.
    // Don't touch window.uploading if we're already on a different recording.
    LocalStream.stopRecording(recordingIdToStop);

    if (screenrecorder) {
      if (screenrecorder.state == "recording") {
        await new Promise((resolve) => {
          screenrecorder.addEventListener("stop", resolve, { once: true });
          screenrecorder.stop();
        });
      }
      screenrecorder = undefined;
    }
  }

  check_force_stop_recording() {
    if ($("#stop_recording_button").length > 0) {
      var data = new FormData();
      data.append("_method", "patch");
      var param = document
        .querySelector("meta[name=csrf-param]")
        .getAttribute("content");
      var token = document
        .querySelector("meta[name=csrf-token]")
        .getAttribute("content");
      data.append(param, token);
      navigator.sendBeacon($("#stop_recording_button").attr("href"), data);
      console.log("STOPPING");
    } else {
      console.log("-- not recording");
    }
  }

  show_now_recording(show_already_recording = false) {
    console.log("show recording.....");

    // Prevent duplicate calls while recording is already active
    if (window.uploading === true) {
      console.log(
        "Recording already active, ignoring duplicate show_now_recording call",
      );
      return;
    }

    LocalStream.record();
    $("#room_panel .devices").slideUp(200);
    $("#room_panel .toggle_devices")
      .removeClass("fa-chevron-up")
      .addClass("fa-chevron-down");

    if (producer_role == false) {
      $(".remote_camera_settings").prop("disabled", true);
      $(".remote_microphone_settings").prop("disabled", true);
      $(".enable_echo_button, .disable_echo_button").prop("disabled", true);
      $(".user_reduce_noise").prop("disabled", true);
      $(".user_remove_echo").prop("disabled", true);
      $("#video_setting").prop("disabled", true);
      $("#microphone_setting").prop("disabled", true);
      $(".disable_echo_cancellation, .enable_echo_cancellation").prop(
        "disabled",
        true,
      );
      $("#max_quality_dropdown").prop("disabled", true);
      $("#frame_rate_dropdown").prop("disabled", true);
      $(".video_type_label").addClass("opacity-40");

      $(".echo_row").css("opacity", "0.5");
      $("#toggle_video").addClass("disabled");
      $("#toggle_mic").addClass("disabled");
      $(".settings_cant_be_changed").show();
    }

    window.uploading = true;
    blob_index = 1;
    var new_button = `<span class="mx-1 inline-block" style="text-align: left;width: 151px;margin-top: -4px;padding-left:4px">Recording - <span id="timer" data-time="0" >00:00:00</span></span>`;
    $("#guest_start_recording_button").html(new_button);
    $("#guest_start_recording_button").removeClass();
    $("#guest_start_recording_button").addClass(
      "px-2 text-sm py-2 rounded dark:text-gray-200 inline-block dark:bg-red-800 bg-red-600 text-white",
    );

    if (show_already_recording) {
      this.start_recording_timer();
    } else {
      CountdownTimer.start_countdown_timer();
    }

    // already running so dont call again
    if (local_db.running != true) {
      local_db.check_for_recordings_to_upload();
    }

    // // update recording if camera is off
    // if ($("#video_setting").val() == "off") {
    //   setTimeout(function(){
    //       Rails.ajax({url: `/${subscription_id}/attendee/${$("#attendee_id").val()}/camera_off.js?recording_id=${recording_id}`, type: "patch"})
    //   }, 3000);
    // }

    $("#still_uploading_local, #uploading_past_recordings").hide();
    $("#recording_finished").hide();
  }

  async startScreenCall() {
    if (!navigator.mediaDevices.getDisplayMedia) {
      var error = "Your browser does NOT supports getDisplayMedia API.";
      alert(error);
    } else {
      await navigator.mediaDevices
        .getDisplayMedia({ audio: true, video: true })
        .then(async function (camera) {
          if (!screenClient) {
            screenClient = await AgoraRTC.createClient({
              mode: "rtc",
              codec: "h264",
            });
            await screenClient
              .join(
                options.appid,
                options.channel,
                options.share_screen_token,
                options.share_uid,
              )
              .then(async (uid) => {
                console.log("connected to screentrack channel");
              })
              .catch((error) => {
                Rails.ajax({
                  url: `/${subscription_id}/attendee/${options.share_uid}/remove_share_screen_attendee.js`,
                  type: "delete",
                  error: function (xhr, status, ajaxError) {
                    console.error(
                      "Failed to remove screen share attendee on join error:",
                      ajaxError,
                    );
                    Rollbar.warning(
                      "screen_share_join_error_remove_attendee_failed",
                      {
                        share_uid: options.share_uid,
                        status,
                        error: ajaxError.toString(),
                      },
                    );
                  },
                });
                console.error("screen_share_error1 " + error.message);
                alert(
                  "Unable to share your screen. \n\nYou can always contact our support for help by emailing support@iris.fm. \n\n" +
                    error.message,
                );
              });
          }
          screenTracks.videoTrack = undefined;
          screenTracks.audioTrack = undefined;

          screenstream = camera;
          try {
            screenTracks.audioTrack = AgoraRTC.createCustomAudioTrack({
              mediaStreamTrack: camera.getAudioTracks()[0],
            });
          } catch (error) {
            console.log(error);
            console.log("Cant create audio track for screen sharing");
          }
          try {
            // set constraints for smaller resolution?
            screenTracks.videoTrack = AgoraRTC.createCustomVideoTrack({
              mediaStreamTrack: camera.getVideoTracks()[0],
              bitrateMax: 1200,
              bitrateMin: 600,
            });
          } catch (error) {
            console.log(error);
            console.log("Cant create video track for screen sharing");
          }
        })
        .catch(async function (error) {
          if (error.message.includes("Permission denied")) {
            alert(`Sharing screen error: ${error} \n
 Make sure you have allowed your browser to screenshare.
 System Preferences -> Security & Privacy -> Privacy -> Screen Recording`);
          } else if (
            error.message.includes(
              "The request is not allowed by the user agent",
            )
          ) {
            //  NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission."
            alert(`Sharing screen error: ${error} \n
Make sure you have allowed your browser to screenshare.
System Preferences -> Security & Privacy -> Privacy -> Screen Recording`);
          } else if (
            error.message.includes("must be called from a user gesture handler")
          ) {
            //  invalidAccessError: getDisplayMedia must be called from a user gesture handler
            alert(`Sharing screen error: ${error} \n
You try to enable the experimental feature "Enable MDNS Ice Candidates" in Safari settings, advanced, experimental features`);
          } else {
            console.error("Sharing screen error:", error);
            Rollbar.error("Sharing_screen_error", error.message);
            alert(
              `Unable to share your screen: ${error.message}\n\nPlease try:\n1. Refresh the page and try again\n2. Update your browser to the latest version\n3. Contact support@iris.fm for help`,
            );
          }
        });

      if (screenTracks.videoTrack || screenTracks.audioTrack) {
        if (screenTracks.videoTrack) {
          console.log("PUBLISH SCREEN VIDEO TRACK");
          // Remove previous listener to prevent memory leak before adding new one
          screenTracks.videoTrack.off("track-ended", this.stopShareScreen);
          screenTracks.videoTrack.on("track-ended", this.stopShareScreen);
          await screenClient
            .publish(screenTracks.videoTrack)
            .then(async (test) => {
              console.log("published video track");
            })
            .catch((error) => {
              console.log(error);
            });
        }
        if (screenTracks.audioTrack) {
          await screenClient.publish(screenTracks.audioTrack);
        }
        $("#share_screen_button").hide();
        $("#stop_share_screen_button").show();
        var recording_id = $("#recording_id").attr("data-recording-id");
        if (recording_id.length) {
          Rails.ajax({
            url: `/recordings/${recording_id}/update_layout.js?share_uid=${options.share_uid}`,
            type: "patch",
            error: function (xhr, status, error) {
              console.error("Failed to update layout for screen share:", error);
              Rollbar.warning("screen_share_update_layout_failed", {
                recording_id,
                share_uid: options.share_uid,
                status,
                error: error.toString(),
              });
            },
          });
          this.record_screen_share();
        }
        return screenTracks;
      } else {
        Rails.ajax({
          url: `/${subscription_id}/attendee/${options.share_uid}/remove_share_screen_attendee.js`,
          type: "delete",
          error: function (xhr, status, error) {
            console.error("Failed to remove screen share attendee:", error);
            Rollbar.warning("remove_screen_share_attendee_failed", {
              share_uid: options.share_uid,
              status,
              error: error.toString(),
            });
          },
        });
      }
    }
  }

  record_screen_share() {
    // Prevent concurrent screen recording operations
    if (screenrecorder) {
      if (screenrecorder.state == "recording") {
        console.log("Screen recorder already recording, stopping first");
        screenrecorder.addEventListener(
          "stop",
          () => {
            screenrecorder = undefined;
            this.record_screen_share();
          },
          { once: true },
        );
        screenrecorder.stop();
        return;
      }
      screenrecorder = undefined;
    }

    if (!screenstream) {
      console.error("No screen stream available for recording");
      return;
    }

    screenrecorder = new MediaRecorder(screenstream);
    screenrecorder.ondataavailable = function (e) {
      try {
        Rails.ajax({
          url: `/recordings/${$("#recording_id").attr("data-recording-id")}/attendee/${options.share_uid}/file_saved.js`,
          type: "post",
          error: function (xhr, status, error) {
            console.error(
              "Failed to notify server of screen share file_saved:",
              error,
            );
            Rollbar.warning("screen_share_file_saved_notification_failed", {
              share_uid: options.share_uid,
              status,
              error: error.toString(),
            });
          },
        });

        if (!e.data || !e.data.size || e.data.size < 1) {
          Room.show_error_message(
            "Your browser tried to save a recording without any data. Please try a different browser like Google Chrome or else contact support at support@iris.fm.",
          );
          if (!e.data || !e.data.size) {
            console.error("NO DATA ondataavailable _record_screen_share");
            setTimeout(function () {
              Rails.ajax({
                url: `/recordings/${$("#recording_id").attr("data-recording-id")}/attendee/${options.share_uid}/error.js?error=zerofile`,
                type: "post",
                error: function (xhr, status, error) {
                  console.error(
                    "Failed to report screen share zero file error:",
                    error,
                  );
                  Rollbar.error("screen_share_zero_file_error_report_failed", {
                    share_uid: options.share_uid,
                    status,
                    error: error.toString(),
                  });
                },
              });
            }, 1000);
          } else {
            console.error("Too small DATA ondataavailable: " + e.data.size);
            setTimeout(function () {
              Rails.ajax({
                url:
                  `/recordings/${$("#recording_id").attr("data-recording-id")}/attendee/${options.share_uid}/error.js?error=toosmallfile` +
                  e.data.size,
                type: "post",
                error: function (xhr, status, error) {
                  console.error(
                    "Failed to report screen share small file error:",
                    error,
                  );
                  Rollbar.error("screen_share_small_file_error_report_failed", {
                    share_uid: options.share_uid,
                    file_size: e.data.size,
                    status,
                    error: error.toString(),
                  });
                },
              });
            }, 1000);
          }
        } else {
          var current_recording_id = parseInt(
            $("[data-recording-id]").attr("data-recording-id"),
          );
          // Capture screen_blob_index in closure to prevent race conditions
          const capturedIndex = screen_blob_index++;
          LocalStream.save_screen_blob(
            e.data,
            capturedIndex,
            current_recording_id,
          );
        }
      } catch (error) {
        console.error("Error in screen ondataavailable handler:", error);
        Rollbar.error("screen_ondataavailable_handler_error", {
          error: error.toString(),
          message: error.message,
          stack: error.stack,
        });
      }
    };
    screenrecorder.start(15000);
    Rails.ajax({
      url: `/recordings/${$("#recording_id").attr("data-recording-id")}/attendee/${options.share_uid}/recording_started.js`,
      type: "post",
      error: function (xhr, status, error) {
        console.error(
          "Failed to notify server of screen share recording_started:",
          error,
        );
        Rollbar.warning("screen_share_recording_started_notification_failed", {
          share_uid: options.share_uid,
          status,
          error: error.toString(),
        });
      },
    });
  }

  async stopShareScreen() {
    Rails.ajax({
      url: `/${subscription_id}/attendee/${options.share_uid}/remove_share_screen_attendee.js`,
      type: "delete",
      error: function (xhr, status, error) {
        console.error("Failed to remove screen share attendee on stop:", error);
        Rollbar.warning("stop_screen_share_remove_attendee_failed", {
          share_uid: options.share_uid,
          status,
          error: error.toString(),
        });
      },
    });
    for (var trackName in screenTracks) {
      var track = screenTracks[trackName];
      if (track) {
        // Remove event listeners to prevent memory leaks
        if (track.off && typeof track.off === "function") {
          track.off("track-ended");
        }
        track.stop();
        track.close();
        screenTracks[trackName] = undefined;
      }
    }
    if (screenClient) {
      await screenClient.leave();
      screenClient = undefined;
    }
    $("#share_screen_button").show();
    $("#stop_share_screen_button").hide();

    var attendee_id = $("#attendee_id").val();
    var recording_id = $("#recording_id").attr("data-recording-id");
    if (recording_id.length) {
      Rails.ajax({
        url: `/recordings/${recording_id}/update_layout.js?attendee_id=${attendee_id}`,
        type: "patch",
        error: function (xhr, status, error) {
          console.error("Failed to update layout on stop screen share:", error);
          Rollbar.warning("stop_screen_share_update_layout_failed", {
            recording_id,
            attendee_id,
            status,
            error: error.toString(),
          });
        },
      });
    }
    if (screenrecorder) {
      if (screenrecorder.state != "inactive") {
        screenrecorder.stop();
      }
      screenrecorder = undefined;
      Rails.ajax({
        url: `/recordings/${$("#recording_id").attr("data-recording-id")}/attendee/${options.share_uid}/recording_stopped.js`,
        type: "post",
        error: function (xhr, status, error) {
          console.error(
            "Failed to notify server of screen share recording_stopped:",
            error,
          );
          Rollbar.warning(
            "screen_share_recording_stopped_notification_failed",
            { share_uid: options.share_uid, status, error: error.toString() },
          );
        },
      });
    }
    FixVideos.fix_resolution();
  }

  host_error_message(message, room_attendee_id) {
    if (
      $("#is_host").length > 0 &&
      $("#attendee_id").val() != room_attendee_id
    ) {
      this.show_error_message(message);
    }
  }

  show_error_message(message) {
    $("#error_modal_message").html(message);
    $("#error_modal").show();
  }

  async toggleVideo() {
    var attendee_id = $("#attendee_id").val();

    if (event) {
      event.preventDefault();
    }
    // $("#video-btn i").toggleClass('fa-video').toggleClass('fa-video-slash'); // toggle the video icon
    if (currentCam && !$("#video-btn i").hasClass("fa-video")) {
      //enabled
      // $(`.local-player `).addClass("player").show()
      // FixVideos.fix_resolution()

      if (producer_role && $("#mic-btn span").text() == "Mic") {
        $(`.local-player`).addClass("player").show();
      }

      $(`#player-${options.uid} .video_camera_off_icon`).remove();
      $("#video-btn i").removeClass("text-red-600");
      $("#test_video_setting, #video_setting").css("opacity", "1");
      $("#video-btn .fa-video-slash")
        .removeClass("fa-video-slash")
        .addClass("fa-video");
      $(`#player-${options.uid} .fa-video-slash`)
        .removeClass("fa-video-slash")
        .addClass("fa-video");
      $(`#user-row-${options.uid} .fa-video-slash`).hide();
      $(`#user-row-${options.uid} .fa-video`).show();
      $(`#toggle_video .fa-video-slash, #join_modal .fa-video-slash`)
        .removeClass("fa-video-slash")
        .addClass("fa-video");
      $(".settings_option .fa-video, #join_modal .fa-video")
        .removeClass("text-red-500")
        .addClass("text-indigo-500");
      $("#video-btn span").text("Camera");
      // await LocalStream.update()

      if ($("#subscription_id").attr("data-max-width") != "0") {
        if (local_media_stream) {
          if (local_media_stream.getVideoTracks().length > 0) {
            local_media_stream.getVideoTracks()[0].enabled = true;
          } else {
            await LocalStream.update();
          }
        } else {
          await LocalStream.update();
        }
      }

      if (!localTracks.videoTrack) {
        await AgoraRTC.createCameraVideoTrack({ cameraId: currentCam.deviceId })
          .then(async (track) => {
            localTracks.videoTrack = track;
            await localTracks.videoTrack.setEnabled(true);
            if (client.uid) {
              await localTracks.videoTrack.play(`player-${options.uid}`);
              await client.publish(localTracks.videoTrack);
            } else {
              if ($("#pre-local-player").length > 0) {
                await localTracks.videoTrack.play(`pre-local-player`);
              }
            }
            Rails.ajax({
              url: `/${subscription_id}/attendee/${attendee_id}/camera_on.js?recording_id=${$("#recording_id").attr("data-recording-id")}`,
              type: "patch",
              error: function (xhr, status, error) {
                console.error("Failed to notify server of camera_on:", error);
                Rollbar.warning("camera_on_notification_failed", {
                  attendee_id,
                  status,
                  error: error.toString(),
                });
              },
            });
          })
          .catch((error) => {
            // alert(error.message)
          });
      } else {
        if (!localTracks.videoTrack._enabled) {
          await localTracks.videoTrack.setEnabled(true);
          if (client.uid) {
            await localTracks.videoTrack.play(`player-${options.uid}`);
            await client.publish(localTracks.videoTrack);
          } else {
            if ($("#pre-local-player").length > 0) {
              await localTracks.videoTrack.play(`pre-local-player`);
            }
          }
          Rails.ajax({
            url: `/${subscription_id}/attendee/${attendee_id}/camera_on.js?recording_id=${$("#recording_id").attr("data-recording-id")}`,
            type: "patch",
            error: function (xhr, status, error) {
              console.error("Failed to notify server of camera_on (2):", error);
              Rollbar.warning("camera_on_notification_failed_2", {
                attendee_id,
                status,
                error: error.toString(),
              });
            },
          });
        }
      }

      if (localTracks.videoTrack && !this.is_iOS()) {
        try {
          await localTracks.videoTrack.setDevice(currentCam.deviceId);
        } catch (err) {
          console.warn("setDevice failed for", currentCam && currentCam.label, err.message);
          localStorage.removeItem("iris_camera");
          const fallback = cams.find((c) => c.deviceId !== currentCam.deviceId);
          if (fallback) {
            currentCam = fallback;
            await localTracks.videoTrack.setDevice(fallback.deviceId).catch((e) => {
              console.warn("fallback setDevice also failed:", e.message);
            });
          }
        }
      }
      FixVideos.fix_resolution();
      if (currentCam) {
        $(
          `#test_video_setting, #video_setting, #user-row-${options.uid} .remote_camera_settings`,
        ).val(currentCam.label);
      }
      $(`#player-${options.uid}`).show();
      $(`#user-name-${options.uid}`).show();
      // localTracks.videoTrack.play()
    } else {
      //disabled
      $("#video-btn i").addClass("text-red-600");
      $("#test_video_setting, #video_setting").css("opacity", "0.5");
      // $(".fa-video").addClass("fa-video-slash").removeClass("fa-video")

      $("#video-btn .fa-video")
        .removeClass("fa-video")
        .addClass("fa-video-slash");
      $(`#player-${options.uid} .fa-video`)
        .removeClass("fa-video")
        .addClass("fa-video-slash");
      $(`#toggle_video .fa-video`)
        .removeClass("fa-video")
        .addClass("fa-video-slash");

      $(" #join_modal .fa-video")
        .addClass("fa-video-slash")
        .removeClass("fa-video");
      $(".settings_option .fa-video-slash, #join_modal .fa-video-slash")
        .addClass("text-red-500")
        .removeClass("text-indigo-500");

      $("#video-btn span").text("Camera Off");
      $(
        `#test_video_setting, #video_setting, #user-row-${options.uid} .remote_camera_settings`,
      ).val("off");
      $(`#user-row-${options.uid} .fa-video-slash`).show();
      $(`#user-row-${options.uid} .fa-video`).hide();
      // await LocalStream.update()
      if (local_media_stream) {
        if (local_media_stream.getVideoTracks().length > 0) {
          local_media_stream.getVideoTracks()[0].enabled = false;
        }
      }

      if (localTracks.videoTrack) {
        // if (client.uid){ await client.unpublish(localTracks.videoTrack) }
        await localTracks.videoTrack.setEnabled(false);
      }

      Rails.ajax({
        url: `/${subscription_id}/attendee/${attendee_id}/camera_off.js?recording_id=${$("#recording_id").attr("data-recording-id")}`,
        type: "patch",
        error: function (xhr, status, error) {
          console.error("Failed to notify server of camera_off:", error);
          Rollbar.warning("camera_off_notification_failed", {
            attendee_id,
            status,
            error: error.toString(),
          });
        },
      });
      PlayerOptions.addCameraOffIcon($(`#player-${options.uid}`));
      // $(`#user-name-${options.uid}`).hide()
      // $(`#player-${options.uid}`).hide()

      //hide video in case
      // $(`#player-${options.uid} .agora_video_player`).parent().remove()
      // localTracks.videoTrack.stop()
      //remove player for self if producer and all off
      if (producer_role && $("#mic-btn span").text() == "Mic Off") {
        $(`.local-player`).removeClass("player").hide();
      }
      //fix for firefox
      if (localTracks.videoTrack) {
        localTracks.videoTrack.stop();
      }
      await LocalStream.ensureCaptureInvariant({
        source: "toggleVideo_off",
      });
      FixVideos.fix_resolution();
    }
  }

  async toggleMic() {
    var attendee_id = $("#attendee_id").val();
    if (event) {
      event.preventDefault();
    }

    var attendee_id = $("#attendee_id").val();
    // $("#mic-btn i").toggleClass('fa-microphone').toggleClass('fa-microphone-slash'); // toggle the mic icon
    if (currentMic && !$("#mic-btn i").hasClass("fa-microphone")) {
      if (producer_role && $("#mic-btn span").text() == "Mic Off") {
        $(`.local-player`).addClass("player").show();
        FixVideos.fix_resolution();
      }

      $("#mic-btn i").removeClass("text-red-600");
      $("#test_microphone_setting, #microphone_setting").css("opacity", "1");
      $(
        ".settings_option .fa-microphone-slash, #join_modal .fa-microphone-slash",
      )
        .removeClass("text-red-500")
        .addClass("text-indigo-500");
      $(
        "#mic-btn i, .settings_option .fa-microphone-slash, #join_modal .fa-microphone-slash",
      )
        .removeClass("fa-microphone-slash")
        .addClass("fa-microphone");
      $("#mic-btn span").text("Mic");
      if (!localTracks.audioTrack) {
        await AgoraRTC.createMicrophoneAudioTrack(Devices.microphone_config)
          .then((track) => {
            localTracks.audioTrack = track;
            if (currentSpeaker) {
              // localTracks.audioTrack.setPlaybackDevice(currentSpeaker.deviceId)
              // .catch(error => {
              //   console.error("could not setPlaybackDevice 2" + error.message)
              //   // alert(error.message)
              // })
            }
          })
          .catch((error) => {
            console.error("couldnt_create_mic: " + error.message);
            alert(error.message);
          });
      }
      if (localTracks.audioTrack) {
        if (!localTracks.audioTrack._enabled) {
          await localTracks.audioTrack.setEnabled(true);
          if (client.uid) {
            await client.publish(localTracks.audioTrack);
          }
        }
        if (
          !this.is_iOS() &&
          typeof localTracks.audioTrack.setDevice === "function"
        ) {
          await localTracks.audioTrack.setDevice(currentMic.deviceId);
        }
      }

      if (local_media_stream) {
        if (local_media_stream.getAudioTracks().length > 0) {
          local_media_stream.getAudioTracks()[0].enabled = true;
        } else {
          await LocalStream.update();
        }
      } else {
        await LocalStream.update();
      }
      if (currentMic) {
        $(
          `#test_microphone_setting, #microphone_setting, #user-row-${options.uid} .remote_microphone_settings`,
        ).val(currentMic.label);
      }
      Rails.ajax({
        url: `/${subscription_id}/attendee/${attendee_id}/unmute.js`,
        type: "patch",
        error: function (xhr, status, error) {
          console.error("Failed to notify server of unmute:", error);
          Rollbar.warning("unmute_notification_failed", {
            attendee_id,
            status,
            error: error.toString(),
          });
        },
      });
    } else {
      //disabled
      $("#test_microphone_setting, #microphone_setting").css("opacity", "0.5");
      $("#mic-btn i").addClass("text-red-600");
      $(".settings_option .fa-microphone, #join_modal .fa-microphone")
        .addClass("text-red-500")
        .removeClass("text-indigo-500");
      $(
        "#mic-btn i, .settings_option .fa-microphone, #join_modal .fa-microphone",
      )
        .addClass("fa-microphone-slash")
        .removeClass("fa-microphone");
      $("#mic-btn span").text("Mic Off");
      $(
        `#test_microphone_setting, #microphone_setting, #user-row-${options.uid} .remote_microphone_settings`,
      ).val("off");
      if (localTracks.audioTrack) {
        // if (client.uid){ await client.unpublish(localTracks.audioTrack) }
        await localTracks.audioTrack.setEnabled(false);
      }
      // await LocalStream.update()

      if (local_media_stream) {
        if (local_media_stream.getAudioTracks().length > 0) {
          local_media_stream.getAudioTracks()[0].enabled = false;
        }
      }
      await LocalStream.ensureCaptureInvariant({
        source: "toggleMic_off",
      });
      Rails.ajax({
        url: `/${subscription_id}/attendee/${attendee_id}/mute.js`,
        type: "patch",
        error: function (xhr, status, error) {
          console.error("Failed to notify server of mute:", error);
          Rollbar.warning("mute_notification_failed", {
            attendee_id,
            status,
            error: error.toString(),
          });
        },
      });

      if (producer_role && $("#video-btn span").text() == "Camera Off") {
        $(`.local-player`).removeClass("player").hide();
      }
      //fix for firefox
      // localTracks.videoTrack.stop()
      FixVideos.fix_resolution();
    }
  }

  enableEchoCancellation(id = options.uid) {
    const previousAudioOptions = {
      echo_cancellation: options.echo_cancellation,
      noise_reduction: options.noise_reduction,
    };
    options.echo_cancellation = true;
    $(".disable_echo_button").show();
    $(".enable_echo_button").hide();
    $(`#user-row-${id} .user_remove_echo`).addClass(
      "bg-indigo-500 dark:bg-indigo-700",
    );
    $(`#user-row-${id} .user_remove_echo span`).addClass("translate-x-3");
    if (String(id) === String(options.uid)) {
      this.syncLocalAudioSettingUI(id);
      this.runAudioOp(() => this.recreateAudioTrack(previousAudioOptions));
    }
  }

  disableEchoCancellation(id = options.uid) {
    const previousAudioOptions = {
      echo_cancellation: options.echo_cancellation,
      noise_reduction: options.noise_reduction,
    };
    options.echo_cancellation = false;
    $(".disable_echo_button").hide();
    $(".enable_echo_button").show();
    $(`#user-row-${id} .user_remove_echo`).removeClass(
      "bg-indigo-500 dark:bg-indigo-700",
    );
    $(`#user-row-${id} .user_remove_echo span`).removeClass("translate-x-3");
    if (String(id) === String(options.uid)) {
      this.syncLocalAudioSettingUI(id);
      this.runAudioOp(() => this.recreateAudioTrack(previousAudioOptions));
    }
  }

  async showLiveTranscript(message) {
    $("#live_transcript").text(message);

    if (Transcribe.running != true) {
      Transcribe.start();
    }
  }

  enableNoiseReduction(id = options.uid) {
    const previousAudioOptions = {
      echo_cancellation: options.echo_cancellation,
      noise_reduction: options.noise_reduction,
    };
    options.noise_reduction = true;
    $(`#user-row-${id} .user_reduce_noise`).addClass(
      "bg-indigo-500 dark:bg-indigo-700",
    );
    $(`#user-row-${id} .user_reduce_noise span`).addClass("translate-x-3");
    if (String(id) === String(options.uid)) {
      this.syncLocalAudioSettingUI(id);
      this.runAudioOp(() => this.recreateAudioTrack(previousAudioOptions));
    }
  }

  disableNoiseReduction(id = options.uid) {
    const previousAudioOptions = {
      echo_cancellation: options.echo_cancellation,
      noise_reduction: options.noise_reduction,
    };
    options.noise_reduction = false;
    $(`#user-row-${id} .user_reduce_noise`).removeClass(
      "bg-indigo-500 dark:bg-indigo-700",
    );
    $(`#user-row-${id} .user_reduce_noise span`).removeClass("translate-x-3");
    if (String(id) === String(options.uid)) {
      this.syncLocalAudioSettingUI(id);
      this.runAudioOp(() => this.recreateAudioTrack(previousAudioOptions));
    }
  }

  async join_channel() {
    if (producer_role) {
      if ($("#mic-btn i").hasClass("fa-microphone")) {
        await this.toggleMic();
      }
      if ($("#video-btn i").hasClass("fa-video")) {
        await this.toggleVideo();
      }
    }

    await client
      .join(options.appid, options.channel, options.token, options.uid)
      .then(async (uid) => {
        console.log("connected to video channel");
        user_joined_room = true;
        options.uid = uid;

        if (localTracks.videoTrack) {
          if (localTracks.videoTrack._enabled) {
            await client.publish([localTracks.videoTrack]);
            $(`#user-name-${options.uid}`).show();
          } else {
            console.log("dont push cam - not enabled");
          }
        } else {
          console.log("dont push cam");
        }

        if (localTracks.audioTrack) {
          if (localTracks.audioTrack._enabled) {
            await client.publish([localTracks.audioTrack]);
          } else {
            console.log("dont push mic - not enabled");
          }
        } else {
          console.log("dont push mic");
        }
      })
      .catch((error) => {
        if (
          !error.message.includes(
            "Client already in connecting/connected state",
          ) &&
          !error.message.includes("EXCHANGE_SDP_FAILED") &&
          !window.pageUnloading
        ) {
          console.error("agora_join_failure4 " + error.message);
          Rollbar.error(
            "agora_join_failure4",
            error.message,
            options.uid,
            client._cloudProxyServerMode,
          );
          alert(
            "Unable to join the channel. You can try refreshing the webpage, using a different browser, and connecting to a different wifi network. \n\nIf this continues contact our support by emailing support@iris.fm. \n\n" +
              error.message,
          );
        }
      });
  }
}
